import React from "react";
import { ShieldAlert } from "lucide-react";

/** Reusable candidate-name watermark + red integrity banner. Rendered on
 * every timed assessment surface (MCQ / Sales Pitch / Role Play) so any
 * leaked screenshot or screen-recording is traceable back to who took it
 * and roughly when. Two pieces:
 *   1. IntegrityBanner  — sticky red bar at the top of the page
 *   2. IntegrityWatermark — diagonal tiled overlay (pointer-events-none)
 *
 * Both pieces read the logged-in user from `localStorage.vi_user` (set by
 * AuthContext on login). Priority for the watermark identifier:
 *   name (as printed on the VI Employee ID) → email → "candidate"
 */

function _readCurrentUser() {
  try {
    return JSON.parse(localStorage.getItem("vi_user") || "{}") || {};
  } catch { return {}; }
}

function _identifier(u) {
  return (u.name && u.name.trim()) || (u.email && u.email.trim()) || "candidate";
}

export function IntegrityBanner() {
  const u = _readCurrentUser();
  const label = _identifier(u);
  return (
    <div
      className="bg-[#EE2737] text-white text-[11px] uppercase tracking-widest font-bold px-4 py-2 flex items-center justify-center gap-2 -mx-4 sm:-mx-6"
      data-testid="integrity-banner"
      role="alert"
    >
      <ShieldAlert size={14} />
      <span className="text-center">
        Any leaked screenshot is traceable to
        {label !== "candidate" ? <> · <span className="font-mono opacity-90 normal-case tracking-normal">{label}</span></> : <> your VI account</>}
      </span>
    </div>
  );
}

export function IntegrityWatermark({ testid = "integrity-watermark" }) {
  const u = _readCurrentUser();
  const label = _identifier(u);
  const stamp = new Date().toLocaleString();
  return (
    <div
      className="pointer-events-none fixed inset-0 z-40 overflow-hidden select-none"
      aria-hidden="true"
      data-testid={testid}
    >
      <div className="absolute inset-0 flex flex-wrap items-center justify-center gap-24 opacity-[0.03] -rotate-[24deg] scale-[1.4]">
        {Array.from({ length: 36 }).map((_, i) => (
          <span key={i} className="text-[13px] font-bold tracking-widest uppercase text-[#0A0A0A] whitespace-nowrap">
            VI · {label} · {stamp}
          </span>
        ))}
      </div>
    </div>
  );
}
